import { MouseEventHandler, useRef, useState } from "react";
import styled from "styled-components";
import { ImageFill } from "../ImageFill";
import { Text } from "../Text";
import { motion, Variants } from "framer-motion";
import { easings, fill } from "../../styles/utils";
import { Video } from "../../mock/video-wall";
import ReactPlayer from "react-player/lazy";
import { Box } from "../Box";

interface VideoWallVideoProps {
  video: Video;
  selected: boolean;
  onMouseOver?: () => any;
  onMouseLeave?: () => any;
  onMouseDown?: () => any;
  onMouseUp?: () => any;
  onClick: (currentTime: number, domRect: DOMRect) => any;
}

const StyledReactPlayer = styled(ReactPlayer)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
  object-fit: cover;
  pointer-events: none;
`;

const Wrapper = styled.div<{ displayWidth: number }>`
  position: absolute;
  width: ${({ displayWidth }) => displayWidth}vw;
  transform: translate(-50%, -50%);
`;

const PadLeft = styled.div`
  padding-left: 1.5em;
`;

const VideoWrapper = styled.div<{ ar: number }>`
  position: relative;
  padding-top: ${({ ar }) => ar}%;
  box-shadow: 20px 20px 100px 0 rgba(0, 0, 0, 0.6);
  overflow: hidden;
`;

const LabelHolder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  transform: rotate(-90deg) translateX(-100%);
  transform-origin: left top;
  color: white;
`;

const VideoContainer = styled(motion.div)`
  ${fill()}
`;

const PlayerWrapper = styled(Box)`
  position: absolute;
  top: 50%;
  left: 50%;
  aspect-ratio: 16/9;
  transform: translate(-50%, -50%);
`;

const variants: Variants = {
  hide: { opacity: 0 },
  show: { opacity: 1 },
};

export const VideoWallVideo: React.FC<VideoWallVideoProps> = ({
  onMouseOver,
  onMouseLeave,
  onMouseDown,
  onMouseUp,
  onClick,
  video,
  selected,
}) => {
  const elRef = useRef<HTMLDivElement>(null);
  const [init, setInit] = useState(false);
  const [playing, setPlaying] = useState(false);

  const imgSource = useRef(process.env.REACT_APP_MEDIA_URL);
  const player = useRef<ReactPlayer>();

  const handleMouseOver = () => {
    onMouseOver && onMouseOver();
    if (!init) {
      setInit(true);
    }
    console.log("set playing");
    if (player.current) {
      setPlaying(true);
    }
  };

  const handleMouseLeave: React.MouseEventHandler<HTMLDivElement> = (e) => {
    onMouseLeave && onMouseLeave();

    if (player.current) {
      setPlaying(false);
    }
  };

  const handleClick: MouseEventHandler<HTMLDivElement> = (e) => {
    if (player.current && elRef.current) {
      setPlaying(false);
      onClick &&
        onClick(
          player.current.getCurrentTime(),
          elRef.current.getBoundingClientRect()
        );
    }
  };

  return (
    <Wrapper displayWidth={video.displayWidth || 20}>
      <motion.div
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 1.06 }}
        animate={selected ? { scale: 1.06 } : { scale: 1 }}
        transition={{ duration: 0.6, ease: easings.easeOutCubic }}
        onMouseOver={handleMouseOver}
        onMouseLeave={handleMouseLeave}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
        onClick={handleClick}
      >
        <PadLeft>
          <LabelHolder>
            <Text variant="small">Lorem</Text>
          </LabelHolder>
          <VideoWrapper ref={elRef} ar={(video.height / video.width) * 100}>
            <VideoContainer
              variants={variants}
              animate={!init ? "show" : "hide"}
              transition={{ duration: 1 }}
            >
              <ImageFill
                src={imgSource.current + video.previewImg.src}
                fit={video.previewImg.fit}
              ></ImageFill>
            </VideoContainer>
            <PlayerWrapper
              width={video.height > video.width ? "auto" : "110%"}
              height={video.height > video.width ? "110%" : "auto"}
            >
              <StyledReactPlayer
                ref={player}
                controls={false}
                playing={playing}
                muted
                poster="video.previewImg.src"
                loop
                url={video.src}
                width="100%"
                height="100%"
              ></StyledReactPlayer>
            </PlayerWrapper>
          </VideoWrapper>
        </PadLeft>
      </motion.div>
    </Wrapper>
  );
};
